import { useState } from 'react';
import { useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';

import { PinField } from '@speeki/global-ui-components';

import { FetchCaseReportPdfDocument } from '@graphql/generated/graphql';

import { getPath } from '@services/RouteService/paths';

import { useMutation } from '@apollo/client';
import { ShowPin } from '@domains/new-conversation/organisms/ReportMailAndPinForm/ReportMailAndPinForm.styled';
import { yupResolver } from '@hookform/resolvers/yup';

import { CasePinFormWrapper, ContinueButton } from './CasePinForm.styled';

export const CasePinForm = () => {
    const navigate = useNavigate();
    const { caseId, companyCode } = useParams<{
        caseId: string;
        companyCode: string;
    }>();
    const { t } = useTranslation();
    const [isVisible, setIsVisible] = useState(false);

    const {
        control,
        formState: { errors, isSubmitted, isSubmitting, isValid },
        handleSubmit,
    } = useForm<CaseDownloadSchemaForm>({
        mode: 'onChange',
        reValidateMode: 'onChange',
        resolver: yupResolver(CaseDownloadSchema),
    });

    const [fetchCaseReportPdf, { loading }] = useMutation(
        FetchCaseReportPdfDocument,
        {
            context: {
                notifyOnError: true,
            },
            onCompleted: ({ casePdf }) => {
                navigate(getPath({ path: '/download/incidentpdf/file' }), {
                    state: {
                        pdfPassword: casePdf.password,
                        pdfUrl: casePdf.s3Url,
                    },
                });
            },
            onError: () => {},
        },
    );

    const onSubmit = (data: CaseDownloadSchemaForm) => {
        void fetchCaseReportPdf({
            variables: {
                input: {
                    caseId: caseId ?? '',
                    companyCode: companyCode ?? '',
                    pinCode: data.pin || '',
                },
            },
        });
    };

    const {
        field: { name, onChange, ref },
    } = useController({ control, name: 'pin' });

    return (
        <CasePinFormWrapper onSubmit={handleSubmit(onSubmit)}>
            <PinField
                className="htmlToImageIgnore"
                error={!!errors.pin}
                message={t(errors.pin?.message || '')}
                name={name}
                onChange={onChange}
                ref={ref}
                spacing={8}
                testId="oIH2CXi"
                type={isVisible ? 'number' : 'password'}
                xsSpacing={6}
            />
            <ShowPin
                isError={!!errors.pin?.message}
                onClick={() => setIsVisible((prevState) => !prevState)}
            >
                {t(isVisible ? 'HIDE_PIN' : 'SHOW_PIN')}
            </ShowPin>
            <ContinueButton
                disabled={loading || isSubmitting || (!isValid && isSubmitted)}
                isLoading={loading || isSubmitting}
                testId="button_continue"
                variant="primary"
            >
                {t('CONTINUE')}
            </ContinueButton>
        </CasePinFormWrapper>
    );
};

const CaseDownloadSchema = yup.object({
    pin: yup.string().min(6, 'INVALID_PIN'),
});

type CaseDownloadSchemaForm = yup.InferType<typeof CaseDownloadSchema>;
